/**
 * /app/open?to=<path> — "Opening Bundle…", the desktop handoff page (like Slack's and Zoom's launch pages). Tries the
 * desktop app's `bundle://<path>` link once on load; "Open Bundle" tries again, "Continue in browser" goes to the
 * path here, and the bottom offers this device's download. Web-only, signed in or out; loads no third-party scripts.
 */
import { GetApp } from "@bundle/core";
import { Button, Logo, Stack, Surface, Text, useT } from "@bundle/ui";
import { useEffect, useMemo } from "react";
import { currentPlatform, loadAvailability, openDownload, useGetAppState } from "../platform/getApp.ts";

/** Starts the deep link (a custom scheme: the browser asks or hands off; the page stays). */
function launch(path: string): void {
  window.location.href = GetApp.deepLink(path);
}

export function OpenInAppPage() {
  const t = useT();
  const path = useMemo(() => GetApp.handoffTarget(location.search) ?? "/", []);
  const platform = useMemo(() => currentPlatform(), []);
  const { availability } = useGetAppState();
  useEffect(() => {
    loadAvailability();
    launch(path);
  }, [path]);
  const target = GetApp.targetFor(platform, availability);
  const label = GetApp.downloadLabelKey(platform);

  return (
    <Stack grow align="center" justify="center" gap="6" padding="6" background="bg.window" testID="open-in-app">
      <Surface material="elevated" radius="xl" padding="8">
        <Stack gap="4" align="center">
          <Logo variant="tile" size="appTile" />
          <Stack gap="1" align="center">
            <Text variant="title2" weight="bold" align="center">
              {t("getApp.handoff.title")}
            </Text>
            <Text color="text.secondary" align="center">
              {t("getApp.handoff.body")}
            </Text>
          </Stack>
          <Button label={t("getApp.handoff.open")} variant="primary" size="lg" onPress={() => launch(path)} testID="open-in-app-retry" />
          <Button label={t("getApp.handoff.browser")} variant="ghost" onPress={() => window.location.assign(path)} testID="open-in-app-browser" />
        </Stack>
      </Surface>
      <Stack direction="horizontal" gap="3" align="center">
        <Text variant="footnote" color="text.secondary">
          {t("getApp.handoff.noApp")}
        </Text>
        {target !== null && label !== null ? (
          <Button label={t(label)} icon="download" variant="secondary" size="sm" onPress={() => openDownload(target.url)} testID="open-in-app-download" />
        ) : (
          <Button label={t("getApp.handoff.allDownloads")} icon="download" variant="secondary" size="sm" onPress={() => openDownload(GetApp.downloadPageUrl())} testID="open-in-app-download" />
        )}
      </Stack>
    </Stack>
  );
}
